/**
 * PeoplePage — workforce hub.
 *
 * Top-level destination (between Dashboard and Equipment) that consolidates
 * everything PMs/admins need to manage staff:
 *   • Staff register table (was buried inside Management)
 *   • Licensing register — aggregated per-user AHPRA / radiation / BLS / contrast
 *     with status chips and one-click expiring filter
 *   • SOP assignments — who has what assigned, due-date status, completion %
 *   • Onboarding progress per user (placeholder until Phase 3 wires the wizard)
 *   • Invite link / Create account modals (re-exposed here for super-admins
 *     who don't see /management)
 *
 * Accessible to both 'admin' and 'superuser' roles.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Users, UserPlus, ShieldCheck, ClipboardList, AlertTriangle, CheckCircle2, XCircle, GraduationCap, Sparkles, ArrowRight, Trash2, Loader2, ShieldAlert, Shield, Pencil, Save, X, Stethoscope, Search, RefreshCw, KeyRound, Copy as CopyIcon, FilePlus2 } from 'lucide-react';
import Header from '../components/Header';
import BulkActions from '../components/BulkActions';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const C = '#0E2D5C';
const ACCENT = '#42CDD7';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
const today = new Date().toISOString().slice(0, 10);

function daysUntil(iso) {
  if (!iso) return null;
  const ms = new Date(iso) - new Date(today);
  return Math.round(ms / (1000 * 60 * 60 * 24));
}

export function StatusChip({ dateStr }) {
  if (!dateStr) return <span className="text-[10px] text-muted-foreground">—</span>;
  const d = daysUntil(dateStr);
  let color = '#16A34A', label = 'OK';
  if (d == null) { color = '#94a3b8'; label = '—'; }
  else if (d < 0) { color = '#DC2626'; label = `Expired ${Math.abs(d)}d`; }
  else if (d <= 30) { color = '#D97706'; label = `${d}d left`; }
  else if (d <= 90) { color = '#0EA5E9'; label = `${d}d`; }
  return (
    <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
      style={{ backgroundColor: `${color}15`, color }}>{label}</span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Top-level component
// ─────────────────────────────────────────────────────────────────────────────
export default function PeoplePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isPM = user?.role === 'admin' || user?.role === 'superuser';
  const [tab, setTab] = useState('roster');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expiring, setExpiring] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!isPM) { navigate('/'); return; }
    load();
  }, [isPM, navigate]);

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const [u, e, a] = await Promise.all([
        axios.get(`${API}/users`, { withCredentials: true }),
        axios.get(`${API}/users/licensing/expiring?within_days=60`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/sops/assignments`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setUsers(u.data || []);
      const expiringArr = e.data?.users ?? (Array.isArray(e.data) ? e.data : []);
      setExpiring(expiringArr);
      setAssignments(Array.isArray(a.data) ? a.data : []);
    } catch (x) {
      setErr(x.response?.data?.detail || 'Failed to load people data');
    }
    setLoading(false);
  };

  // KPIs
  const total = users.length;
  const active = users.filter(u => u.active !== false).length;
  const diasClinical = users.filter(u =>
    u.active !== false && u.clinical_role && u.clinical_role !== 'NOT_CLINICAL_STAFF'
  ).length;
  const expiringCount = expiring.length;
  const incompleteOnboarding = users.filter(u => !u.totp_enabled || !u.culture_charter_accepted_at).length;

  if (!isPM) return null;

  return (
    <div className="min-h-screen bg-background" data-testid="people-page">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero */}
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-3" style={{ backgroundColor: `${ACCENT}1A`, color: ACCENT }}>
              <Users className="w-3.5 h-3.5" />
              <span className="text-[10px] font-bold uppercase tracking-wider">People · DIAS workforce</span>
            </div>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl font-bold text-foreground">Staff &amp; licensing</h1>
            <p className="mt-3 text-sm sm:text-base text-muted-foreground max-w-2xl">
              The single command-centre for every clinician on the practice register — onboarding, AHPRA / radiation / BLS / contrast licensing, SOP assignments, and audit-ready compliance.
            </p>
          </div>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <KPI icon={ShieldCheck} label="Clinical staff (DIAS)" value={diasClinical} color={C}
            sublabel={`of ${total} logins · ${users.length - diasClinical} hidden`} />
          <KPI icon={CheckCircle2} label="Active logins" value={active} color="#16A34A" />
          <KPI icon={AlertTriangle} label="Licences expiring 60d" value={expiringCount} color={expiringCount > 0 ? '#DC2626' : '#16A34A'} />
          <KPI icon={Sparkles} label="Onboarding incomplete" value={incompleteOnboarding} color={incompleteOnboarding > 0 ? '#D97706' : '#16A34A'} />
        </div>

        {/* Tabs + bulk-actions row */}
        <div className="flex items-center justify-between gap-2 flex-wrap mb-4">
          <div className="flex items-center gap-1 flex-wrap" data-testid="people-tabs">
            {[
              { k: 'roster',     l: 'Staff roster',       Icon: Users },
              { k: 'licensing',  l: 'Licensing register', Icon: ShieldCheck },
              { k: 'sops',       l: 'SOP assignments',    Icon: ClipboardList },
              { k: 'onboarding', l: 'Onboarding',         Icon: GraduationCap },
            ].map(t => (
              <button key={t.k} onClick={() => setTab(t.k)}
                data-testid={`people-tab-${t.k}`}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 transition ${
                  tab === t.k ? 'text-white' : 'text-muted-foreground hover:text-foreground'
                }`}
                style={tab === t.k ? { backgroundColor: ACCENT } : {}}>
                <t.Icon className="w-3 h-3" /> {t.l}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <BulkActions resource="users" title="Staff register" readOnly color={ACCENT} />
            <button onClick={() => setInviteOpen(true)} data-testid="people-invite-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white rounded-xl"
              style={{ backgroundColor: ACCENT }}>
              <UserPlus className="w-4 h-4" /> Invite / create
            </button>
          </div>
        </div>

        {err && (
          <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-xs text-red-600" data-testid="people-error">
            {err}
          </div>
        )}

        {loading ? (
          <div className="bg-card border border-border rounded-2xl p-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : tab === 'roster' ? (
          <RosterTable users={users} reload={load} currentUser={user} />
        ) : tab === 'licensing' ? (
          <LicensingTable users={users} expiring={expiring} />
        ) : tab === 'sops' ? (
          <SOPAssignmentsTable users={users} assignments={assignments} />
        ) : (
          <OnboardingTable users={users} />
        )}

        <p className="mt-6 text-[11px] text-muted-foreground inline-flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" /> Wizard-style new-staff onboarding rolls out in Phase 3 — meanwhile you can invite users from this page and they'll be guided through TOTP + Culture Charter on first login.
        </p>
      </div>

      {inviteOpen && (
        <InviteOrCreateModal
          onClose={() => setInviteOpen(false)}
          onDone={() => { setInviteOpen(false); load(); }}
          accent={ACCENT}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Invite / Create staff — proper modal (was previously a placeholder pointing
// back to /management). Two-mode tabbed flow:
//   • Invite link — generates a single-use invite URL the new staff member
//     opens to set their own password. Email also fires (best-effort) via Resend.
//   • Create account — caller chooses an initial password directly (typical
//     when the staff member is sitting next to you). User still does TOTP +
//     Charter on first login.
// In BOTH flows the caller can tick SOPs to auto-assign on first login. A
// "Tick role defaults" button pre-fills the picker from /role-sop-defaults
// (e.g. all radiographers automatically get the radiation-safety SOP bundle).
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// Clinical role (DIAS profession) vs App role (permissions) — two independent
// fields. See backend routes/users.py for the canonical value list + mapping.
// ─────────────────────────────────────────────────────────────────────────────
const CLINICAL_ROLE_OPTIONS = [
  { v: 'practice_manager',     l: 'Practice manager' },
  { v: 'radiologist',          l: 'Radiologist' },
  { v: 'radiographer',         l: 'Radiographer' },
  { v: 'sonographer',          l: 'Sonographer' },
  { v: 'nurse',                l: 'Nurse' },
  { v: 'reception',            l: 'Reception / front office' },
  { v: 'administrative_staff', l: 'Administrative staff' },
  { v: 'NOT_CLINICAL_STAFF',   l: 'Not a clinical staff member (hide from DIAS)' },
];
const CLINICAL_ROLE_LABEL = Object.fromEntries(CLINICAL_ROLE_OPTIONS.map(o => [o.v, o.l]));
// Profession-specific accent colors — mirrors the section palette so PMs can
// read the register at a glance.
const CLINICAL_ROLE_COLOR = {
  practice_manager:     '#7C3AED', // purple — leadership
  radiologist:          '#0EA5E9', // blue — medical
  radiographer:         '#16A34A', // green — imaging tech
  sonographer:          '#06B6D4', // teal — ultrasound
  nurse:                '#DC2626', // red — clinical support
  reception:            '#D97706', // amber — front-of-house
  administrative_staff: '#64748B', // slate — back-office
};
// Default mapping from app role → sensible clinical-role guess, mirroring the
// backend's APP_ROLE_TO_CLINICAL. Used to auto-sync clinical_role in the form
// when the user hasn't explicitly picked one.
const CLINICAL_ROLE_DEFAULTS = {
  admin:        'NOT_CLINICAL_STAFF',
  superuser:    'practice_manager',
  radiologist:  'radiologist',
  radiographer: 'radiographer',
  sonographer:  'sonographer',
  nursing:      'nurse',
  reception:    'reception',
};

const ROLE_OPTIONS_FOR_INVITE = [
  { v: 'superuser',    l: 'Super user (PM) — admin-level: user mgmt, compliance, everything' },
  { v: 'radiologist',  l: 'Radiologist' },
  { v: 'reception',    l: 'Reception' },
  { v: 'radiographer', l: 'Radiographer' },
  { v: 'sonographer',  l: 'Sonographer' },
  { v: 'nursing',      l: 'Nursing' },
];

function InviteOrCreateModal({ onClose, onDone, accent }) {
  const [mode, setMode] = useState('invite'); // 'invite' | 'create'
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('reception');
  const [clinicalRole, setClinicalRole] = useState('reception');
  const [sopAssignments, setSopAssignments] = useState([]); // [{sop_id, due_date}]
  const [sopsList, setSopsList] = useState([]);
  const [roleDefaults, setRoleDefaults] = useState({ bundles: {}, default_due_days: 14 });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [createdInvite, setCreatedInvite] = useState(null);

  const defaultDue = (() => {
    const d = new Date(); d.setDate(d.getDate() + (roleDefaults.default_due_days || 14));
    return d.toISOString().slice(0, 10);
  })();

  useEffect(() => {
    axios.get(`${API}/sops`, { withCredentials: true })
      .then(r => setSopsList((r.data || []).filter(s => (s.category || '').toLowerCase() !== 'test')))
      .catch(() => {});
    axios.get(`${API}/role-sop-defaults`, { withCredentials: true })
      .then(r => setRoleDefaults(r.data || { bundles: {}, default_due_days: 14 }))
      .catch(() => {});
  }, []);

  const applyRoleDefaults = (r = role) => {
    const ids = roleDefaults.bundles?.[r] || [];
    const due = (() => { const d = new Date(); d.setDate(d.getDate() + (roleDefaults.default_due_days || 14)); return d.toISOString().slice(0, 10); })();
    setSopAssignments(ids.map(sop_id => ({ sop_id, due_date: due })));
  };

  const toggleSop = (sop_id) => {
    setSopAssignments(prev => {
      const has = prev.find(r => r.sop_id === sop_id);
      if (has) return prev.filter(r => r.sop_id !== sop_id);
      return [...prev, { sop_id, due_date: defaultDue }];
    });
  };
  const setDue = (sop_id, due_date) => {
    setSopAssignments(prev => prev.map(r => r.sop_id === sop_id ? { ...r, due_date } : r));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const payload = { email: email.trim(), name: name.trim(), role, sop_assignments: sopAssignments };
      let createdUserId = null;
      if (mode === 'invite') {
        const r = await axios.post(`${API}/users/invite`, payload, { withCredentials: true });
        setCreatedInvite(r.data);
        // invite-mode: clinical_role is pre-seeded server-side from app role; PM
        // can edit post-signup if they want to override.
        return;
      } else {
        if (!password || password.length < 8) throw new Error('Password must be at least 8 characters.');
        const r = await axios.post(`${API}/users/create`, { ...payload, password }, { withCredentials: true });
        createdUserId = r.data?.id;
      }
      // For directly-created users, override clinical_role from the form if the
      // caller picked something different from the app-role default.
      if (createdUserId && clinicalRole) {
        await axios.patch(`${API}/users/${createdUserId}/clinical-role`,
          { clinical_role: clinicalRole }, { withCredentials: true });
      }
      onDone();
    } catch (x) {
      setErr(x?.response?.data?.detail || x.message || 'Failed.');
    }
    setBusy(false);
  };

  const copyLink = () => navigator.clipboard.writeText(createdInvite?.invite_link || '');

  // ─── Invite-link "success" view ───
  if (createdInvite) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="invite-success">
        <div className="absolute inset-0 bg-black/50" onClick={onDone} />
        <div className="relative bg-background border border-border rounded-2xl p-5 w-full max-w-lg space-y-3">
          <h3 className="font-['Outfit'] text-lg font-bold text-foreground">Invite created · {createdInvite.email}</h3>
          {createdInvite.email_status === 'failed' && (
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-2.5 text-xs text-amber-600">
              ⚠ Email delivery failed (likely Resend domain not verified). <strong>Copy the link below and send it manually.</strong>
            </div>
          )}
          <div className="flex items-center gap-2 bg-muted border border-border rounded-lg p-2">
            <input readOnly value={createdInvite.invite_link} data-testid="invite-link-input"
              className="flex-1 bg-transparent text-xs text-foreground font-mono outline-none" />
            <button onClick={copyLink} data-testid="invite-copy-btn"
              className="text-xs font-semibold px-2.5 py-1 rounded border border-border">Copy</button>
          </div>
          <p className="text-[11px] text-muted-foreground">Email this link to {createdInvite.email}. Expires in 7 days. They'll set a password, then complete TOTP + Charter.</p>
          <div className="flex justify-end gap-2 pt-2 border-t border-border">
            <button onClick={() => { setCreatedInvite(null); setEmail(''); setName(''); setSopAssignments([]); }}
              className="text-sm px-3 py-1.5 rounded-lg border border-border">Create another</button>
            <button onClick={onDone} data-testid="invite-done"
              className="text-sm font-semibold px-4 py-1.5 rounded-lg text-white" style={{ backgroundColor: accent }}>
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Form view ───
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="invite-create-modal">
      <div className="absolute inset-0 bg-black/50" onClick={busy ? undefined : onClose} />
      <form onSubmit={submit}
        className="relative bg-background border border-border rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto p-5 shadow-2xl space-y-3">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 pb-3 border-b border-border">
          <div>
            <h3 className="font-['Outfit'] text-lg font-bold text-foreground">Invite or create staff</h3>
            <p className="text-[11px] text-muted-foreground">Choose a mode, fill the basics, optionally pre-assign SOPs.</p>
          </div>
          <button type="button" onClick={onClose} disabled={busy}
            className="text-muted-foreground hover:text-foreground p-1 rounded-md disabled:opacity-50" data-testid="invite-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Mode tabs */}
        <div className="flex items-center gap-1 bg-muted/40 p-1 rounded-lg w-fit">
          {[
            { k: 'invite', l: 'Invite link', desc: 'They set their own password' },
            { k: 'create', l: 'Create directly', desc: 'You set the initial password' },
          ].map(t => (
            <button type="button" key={t.k} onClick={() => setMode(t.k)}
              data-testid={`invite-mode-${t.k}`}
              className={`text-xs font-semibold px-3 py-1.5 rounded-md transition ${mode === t.k ? 'text-white' : 'text-muted-foreground hover:text-foreground'}`}
              style={mode === t.k ? { backgroundColor: accent } : undefined}
              title={t.desc}>
              {t.l}
            </button>
          ))}
        </div>

        {/* Basics */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] font-semibold text-muted-foreground">Full name</label>
            <input value={name} onChange={e => setName(e.target.value)} required disabled={busy}
              data-testid="invite-name"
              className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2" />
          </div>
          <div>
            <label className="text-[10px] font-semibold text-muted-foreground">Email</label>
            <input value={email} onChange={e => setEmail(e.target.value)} required disabled={busy} type="email"
              data-testid="invite-email"
              className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2" />
          </div>
          <div className="col-span-2">
            <label className="text-[10px] font-semibold text-muted-foreground">App role <span className="font-normal text-muted-foreground/70">· permissions in this app</span></label>
            <select value={role} onChange={e => {
                const newRole = e.target.value;
                setRole(newRole);
                // Auto-sync clinical role to match, unless the PM has picked
                // something different already (we only auto-sync if the current
                // clinicalRole matches what the PREVIOUS role would have mapped to).
                const prevMapped = CLINICAL_ROLE_DEFAULTS[role];
                if (clinicalRole === prevMapped) {
                  setClinicalRole(CLINICAL_ROLE_DEFAULTS[newRole] || 'NOT_CLINICAL_STAFF');
                }
              }}
              data-testid="invite-role" disabled={busy}
              className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2">
              {ROLE_OPTIONS_FOR_INVITE.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
            <p className="text-[10px] text-muted-foreground mt-1">Admin role can't be set here — create as superuser, then use Edit → Elevate to Admin.</p>
          </div>
          <div className="col-span-2">
            <label className="text-[10px] font-semibold text-muted-foreground">Clinical role <span className="font-normal text-muted-foreground/70">· profession shown on DIAS / Kestral / licensing register</span></label>
            <select value={clinicalRole} onChange={e => setClinicalRole(e.target.value)}
              data-testid="invite-clinical-role" disabled={busy}
              className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2">
              {CLINICAL_ROLE_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
            <p className="text-[10px] text-muted-foreground mt-1">Pick <strong>Not a clinical staff member</strong> for admin-only logins — keeps them out of DIAS accreditation reports.</p>
          </div>
          {mode === 'create' && (
            <div className="col-span-2">
              <label className="text-[10px] font-semibold text-muted-foreground">Initial password (min 8 chars)</label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} required minLength={8} disabled={busy}
                data-testid="invite-password"
                className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2" />
            </div>
          )}
        </div>

        {/* SOP assignments picker */}
        <div className="border border-border rounded-lg" data-testid="invite-sop-picker">
          <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-border bg-muted/30 flex-wrap">
            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Auto-assign SOPs · {sopAssignments.length} / {sopsList.length} ticked
            </span>
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => applyRoleDefaults(role)} data-testid="invite-sop-defaults"
                title={`Apply the default bundle for ${role}`}
                className="text-[10px] font-semibold px-2 py-0.5 rounded-md border border-border hover:bg-accent">Tick role defaults</button>
              <button type="button" onClick={() => setSopAssignments(sopsList.map(s => ({ sop_id: s.sop_id, due_date: defaultDue })))}
                className="text-[10px] font-semibold px-2 py-0.5 rounded-md border border-border hover:bg-accent">Tick all</button>
              <button type="button" onClick={() => setSopAssignments([])}
                className="text-[10px] font-semibold px-2 py-0.5 rounded-md border border-border hover:bg-accent">Clear</button>
            </div>
          </div>
          <ul className="max-h-60 overflow-y-auto">
            {sopsList.length === 0 ? (
              <li className="px-3 py-3 text-[11px] text-muted-foreground">No SOPs available yet.</li>
            ) : sopsList.map(s => {
              const row = sopAssignments.find(r => r.sop_id === s.sop_id);
              const checked = !!row;
              return (
                <li key={s.sop_id}
                  className="grid grid-cols-[auto_1fr_130px] items-center gap-3 px-3 py-1.5 border-b border-border/40 last:border-0 hover:bg-accent/30">
                  <input type="checkbox" checked={checked} onChange={() => toggleSop(s.sop_id)}
                    data-testid={`invite-sop-${s.sop_id}`}
                    className="w-4 h-4 rounded border-border" />
                  <div className="min-w-0">
                    <span className="text-xs text-foreground font-medium block truncate">{s.title}</span>
                    <span className="text-[10px] text-muted-foreground">{s.category}{s.version ? ` · v${s.version}` : ''}</span>
                  </div>
                  <input type="date" value={row?.due_date || ''} onChange={e => setDue(s.sop_id, e.target.value)}
                    disabled={!checked}
                    className="text-[11px] bg-background border border-border rounded-md px-2 py-1 disabled:opacity-40" />
                </li>
              );
            })}
          </ul>
        </div>

        {err && <p className="text-xs text-red-600" data-testid="invite-error">{err}</p>}

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 pt-3 border-t border-border">
          <button type="button" onClick={onClose} disabled={busy}
            className="text-sm px-3 py-1.5 rounded-lg border border-border disabled:opacity-50">Cancel</button>
          <button type="submit" disabled={busy || !email.trim() || !name.trim()}
            data-testid="invite-submit"
            className="text-sm font-semibold px-4 py-1.5 rounded-lg text-white inline-flex items-center gap-1.5 disabled:opacity-40"
            style={{ backgroundColor: accent }}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />}
            {busy ? 'Saving…' : (mode === 'invite' ? 'Generate invite link' : 'Create account')}
          </button>
        </div>
      </form>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-tables
// ─────────────────────────────────────────────────────────────────────────────
function RosterTable({ users, reload, currentUser }) {
  const isPM = currentUser?.role === 'admin' || currentUser?.role === 'superuser';
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetResult, setResetResult] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [pickTarget, setPickTarget] = useState(null);
  const [busyLookup, setBusyLookup] = useState(null); // user_id currently re-looking-up

  const toggleOne = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAll = (e) => {
    if (e.target.checked) setSelected(new Set(users.map(u => u.id)));
    else setSelected(new Set());
  };
  const allSelected = users.length > 0 && selected.size === users.length;
  const someSelected = selected.size > 0 && selected.size < users.length;

  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden" data-testid="people-roster-table">
      {/* Bulk-action toolbar — shown only when ≥1 row selected */}
      {isPM && selected.size > 0 && (
        <div className="flex items-center gap-3 px-4 py-2.5 border-b border-border"
          data-testid="people-bulk-toolbar"
          style={{ backgroundColor: `${ACCENT}10` }}>
          <span className="text-xs font-semibold text-foreground" data-testid="people-bulk-count">
            {selected.size} selected
          </span>
          <button onClick={() => setBulkOpen(true)} data-testid="people-bulk-edit-btn"
            className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-md text-white"
            style={{ backgroundColor: ACCENT }}>
            <Pencil className="w-3 h-3" /> Bulk-edit licensing
          </button>
          <button onClick={() => setSelected(new Set())} data-testid="people-bulk-clear"
            className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground">
            <X className="w-3 h-3" /> Clear selection
          </button>
        </div>
      )}

      <table className="w-full text-sm">
        <thead className="bg-muted/40">
          <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            {isPM && (
              <th className="px-3 py-3 w-8">
                <input type="checkbox" checked={allSelected}
                  ref={el => { if (el) el.indeterminate = someSelected; }}
                  onChange={toggleAll}
                  data-testid="people-select-all"
                  className="w-4 h-4 accent-current" />
              </th>
            )}
            <th className="px-4 py-3">Staff</th>
            <th className="px-4 py-3">App role</th>
            <th className="px-4 py-3">Clinical role</th>
            <th className="px-4 py-3" title="Two-factor authentication enrolled">2FA</th>
            <th className="px-4 py-3" title="Culture Charter — practice code-of-conduct / privacy / patient-safety policy every staff member accepts on first login. Auditable for DIAS onboarding.">Charter</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3" data-testid="people-col-medicare">Medicare</th>
            {isPM && <th className="px-4 py-3 text-right">Actions</th>}
          </tr>
        </thead>
        <tbody>
          {users.map((u, i) => (
            <tr key={u.id} className={`border-t border-border ${selected.has(u.id) ? 'bg-cyan-500/5' : ''}`} data-testid={`people-row-${i}`}>
              {isPM && (
                <td className="px-3 py-2.5">
                  <input type="checkbox" checked={selected.has(u.id)}
                    onChange={() => toggleOne(u.id)}
                    data-testid={`people-select-row-${i}`}
                    className="w-4 h-4 accent-current" />
                </td>
              )}
              <td className="px-4 py-2.5">
                <div className="font-medium text-sm text-foreground whitespace-nowrap">{u.name || '—'}</div>
                <div className="text-xs text-muted-foreground whitespace-nowrap">{u.email}</div>
              </td>
              <td className="px-4 py-2.5">
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                  style={{ backgroundColor: u.role === 'admin' ? '#DC262615' : u.role === 'superuser' ? `${ACCENT}15` : '#64748B15',
                           color:           u.role === 'admin' ? '#DC2626'  : u.role === 'superuser' ? ACCENT     : '#64748B' }}>
                  {u.role}
                </span>
              </td>
              <td className="px-4 py-2.5 whitespace-nowrap" data-testid={`people-clinical-role-${i}`}>
                {u.clinical_role === 'NOT_CLINICAL_STAFF' ? (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-muted text-muted-foreground whitespace-nowrap"
                    title="Hidden from DIAS / Kestral / licensing register">
                    Hidden — DIAS
                  </span>
                ) : u.clinical_role ? (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full whitespace-nowrap"
                    style={{ backgroundColor: `${CLINICAL_ROLE_COLOR[u.clinical_role] || '#64748B'}15`,
                             color: CLINICAL_ROLE_COLOR[u.clinical_role] || '#64748B' }}
                    title="Profession shown on DIAS / Kestral / licensing register">
                    <Stethoscope className="w-2.5 h-2.5" />
                    {CLINICAL_ROLE_LABEL[u.clinical_role] || u.clinical_role}
                  </span>
                ) : <span className="text-[10px] text-muted-foreground italic">—</span>}
              </td>
              <td className="px-4 py-2.5">
                {u.totp_enabled ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <XCircle className="w-4 h-4 text-muted-foreground/50" />}
              </td>
              <td className="px-4 py-2.5">
                {u.culture_charter_accepted_at ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <XCircle className="w-4 h-4 text-muted-foreground/50" />}
              </td>
              <td className="px-4 py-2.5">
                <span className={`text-[10px] font-bold uppercase tracking-wider ${u.active !== false ? 'text-green-600' : 'text-red-500'}`}>
                  {u.active !== false ? 'Active' : 'Inactive'}
                </span>
              </td>
              <td className="px-4 py-2.5" data-testid={`people-medicare-cell-${i}`}>
                <MedicareCell user={u} isPM={isPM}
                  onPick={() => setPickTarget(u)}
                  onLookup={async () => {
                    setBusyLookup(u.id);
                    try { await axios.post(`${API}/users/${u.id}/medicare-lookup`, {}, { withCredentials: true }); }
                    finally { setBusyLookup(null); reload(); }
                  }}
                  busy={busyLookup === u.id} />
              </td>
              {isPM && (
                <td className="px-4 py-2.5 text-right">
                  <div className="inline-flex items-center gap-1.5">
                    <button onClick={() => setEditTarget(u)}
                      data-testid={`people-row-edit-${i}`}
                      title="Edit name, role, status, and licensing details"
                      className="text-[11px] font-semibold inline-flex items-center gap-1 px-2 py-1 rounded-md border border-border text-muted-foreground hover:text-foreground hover:bg-muted/40">
                      <Pencil className="w-3 h-3" /> Edit
                    </button>
                    {u.id === currentUser?.id ? (
                      <span className="text-[10px] text-muted-foreground italic ml-1">you</span>
                    ) : (
                      <>
                        <button onClick={() => setResetTarget(u)}
                          data-testid={`people-row-reset-pw-${i}`}
                          title="Generate a new temp password + email it to this staff member"
                          className="text-[11px] font-semibold inline-flex items-center gap-1 px-2 py-1 rounded-md border border-amber-500/30 text-amber-700 hover:bg-amber-50">
                          <KeyRound className="w-3 h-3" /> Reset pw
                        </button>
                        <button onClick={() => setDeleteTarget(u)}
                          data-testid={`people-row-delete-${i}`}
                          title="Permanently delete this user"
                          className="text-[11px] font-semibold inline-flex items-center gap-1 px-2 py-1 rounded-md border border-red-500/30 text-red-500 hover:bg-red-500/10">
                          <Trash2 className="w-3 h-3" /> Delete
                        </button>
                      </>
                    )}
                  </div>
                </td>
              )}
            </tr>
          ))}
          {users.length === 0 && (
            <tr><td colSpan={isPM ? 9 : 7} className="px-4 py-10 text-center text-sm text-muted-foreground">No staff registered yet.</td></tr>
          )}
        </tbody>
      </table>

      {editTarget && (
        <EditUserModal user={editTarget} currentUser={currentUser}
          onClose={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); reload(); }} />
      )}

      {pickTarget && (
        <MedicarePickModal user={pickTarget}
          onClose={() => setPickTarget(null)}
          onSaved={() => { setPickTarget(null); reload(); }} />
      )}

      {bulkOpen && (
        <BulkLicensingModal
          users={users.filter(u => selected.has(u.id))}
          onClose={() => setBulkOpen(false)}
          onSaved={() => { setBulkOpen(false); setSelected(new Set()); reload(); }} />
      )}

      {deleteTarget && (
        <DeleteUserModal user={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => { setDeleteTarget(null); reload(); }} />
      )}

      {resetTarget && (
        <ResetPasswordConfirmModal user={resetTarget}
          onClose={() => setResetTarget(null)}
          onDone={(data) => { setResetTarget(null); setResetResult(data); }} />
      )}

      {resetResult && (
        <ResetPasswordResultModal data={resetResult}
          onClose={() => setResetResult(null)} />
      )}
    </div>
  );
}

/**
 * Admin-side edit modal — every field on a staff record that a PM might
 * need to maintain manually instead of waiting for the user to onboard.
 *
 * Fields:
 *   • Display name (PATCH /users/{id}/profile)
 *   • Role             (PUT  /users/{id}/role)         — admin role is locked
 *                                                          to its own panel
 *   • Active status    (PUT  /users/{id}/activate or
 *                       DELETE /users/{id})
 *   • Licensing block  (PATCH /users/{id}/licensing)   — AHPRA, ASAR,
 *                                                          radiation,
 *                                                          BLS, contrast,
 *                                                          accreditations,
 *                                                          CPD year/hours
 *
 * The Save handler fires only the patches whose values changed so we don't
 * overwrite untouched fields. Errors are surfaced inline; the modal stays open
 * so the PM can fix and retry.
 */
const ROLE_OPTIONS = ['radiographer', 'sonographer', 'radiologist', 'nursing', 'reception', 'superuser'];
const LICENSING_FIELDS = [
  { k: 'ahpra_number',                  label: 'AHPRA registration #',     placeholder: 'MED0001234567' },
  { k: 'ahpra_expiry',                  label: 'AHPRA expiry',             type: 'date' },
  { k: 'asar_number',                   label: 'ASAR # (sonographers)',    placeholder: '12345' },
  { k: 'asar_expiry',                   label: 'ASAR expiry',              type: 'date' },
  { k: 'radiation_licence_no',          label: 'Radiation licence #',      placeholder: 'RAD-12345' },
  { k: 'radiation_licence_expiry',      label: 'Radiation licence expiry', type: 'date' },
  { k: 'bls_expiry',                    label: 'BLS / CPR expiry',         type: 'date' },
  { k: 'contrast_accreditation_expiry', label: 'Contrast accreditation expiry', type: 'date' },
];

function EditUserModal({ user, currentUser, onClose, onSaved }) {
  const isAdminTarget = user.role === 'admin';
  const isSelf = user.id === currentUser?.id;
  const isCallerAdmin = currentUser?.role === 'admin';
  // Elevate-to-admin is only offered when:
  //   • caller is an admin (only admins can grant admin)
  //   • target is currently an active superuser (forces deliberate two-step path)
  //   • target is not the caller themselves (no self-elevation)
  const canElevateToAdmin = isCallerAdmin && !isSelf && user.role === 'superuser' && user.active !== false;

  const [name, setName] = useState(user.name || '');
  const [role, setRole] = useState(user.role || 'radiographer');
  const [clinicalRole, setClinicalRole] = useState(user.clinical_role || CLINICAL_ROLE_DEFAULTS[user.role] || 'NOT_CLINICAL_STAFF');
  const [active, setActive] = useState(user.active !== false);
  const initialLic = user.licensing || {};
  const [lic, setLic] = useState({
    ahpra_number: initialLic.ahpra_number || '',
    ahpra_expiry: initialLic.ahpra_expiry || '',
    asar_number: initialLic.asar_number || '',
    asar_expiry: initialLic.asar_expiry || '',
    radiation_licence_no: initialLic.radiation_licence_no || '',
    radiation_licence_expiry: initialLic.radiation_licence_expiry || '',
    bls_expiry: initialLic.bls_expiry || '',
    contrast_accreditation_expiry: initialLic.contrast_accreditation_expiry || '',
    accreditations: (initialLic.accreditations || []).join(', '),
    cpd_hours_ytd: initialLic.cpd_hours_ytd || 0,
    cpd_year: initialLic.cpd_year || new Date().getFullYear(),
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [savedFlag, setSavedFlag] = useState(false);

  const setLicField = (k, v) => setLic(prev => ({ ...prev, [k]: v }));

  // Practice-check audit state — driven from user.licensing on first render
  // and refreshed locally when the PM hits "Mark checked".
  const initialPracticeChecked = initialLic.practice_checked_at || '';
  const initialPracticeDue = initialLic.practice_review_due_at || '';
  const initialPracticeBy = initialLic.practice_checked_by || '';
  const [practiceCheckedAt, setPracticeCheckedAt] = useState(initialPracticeChecked);
  const [practiceReviewDueAt, setPracticeReviewDueAt] = useState(initialPracticeDue);
  const [practiceCheckedBy, setPracticeCheckedBy] = useState(initialPracticeBy);
  const [markingChecked, setMarkingChecked] = useState(false);

  const markPracticeChecked = async () => {
    setMarkingChecked(true); setErr(null);
    try {
      const r = await axios.post(
        `${API}/users/${user.id}/licensing/mark-checked`,
        null,
        { withCredentials: true },
      );
      setPracticeCheckedAt(r.data.practice_checked_at || '');
      setPracticeReviewDueAt(r.data.practice_review_due_at || '');
      setPracticeCheckedBy(r.data.practice_checked_by || '');
    } catch (e) {
      setErr(e.response?.data?.detail || 'Mark-checked failed.');
    }
    setMarkingChecked(false);
  };

  const save = async () => {
    setBusy(true); setErr(null); setSavedFlag(false);
    try {
      // 1. Name (only if changed and non-empty)
      if (name.trim() && name.trim() !== (user.name || '').trim()) {
        await axios.patch(`${API}/users/${user.id}/profile`,
          { name: name.trim() },
          { withCredentials: true });
      }

      // 2. Role (only if changed; admin role is immutable via this endpoint)
      if (!isAdminTarget && role !== user.role) {
        await axios.put(`${API}/users/${user.id}/role`, null,
          { params: { role }, withCredentials: true });
      }

      // 2b. Clinical role (DIAS profession) — independent from app role.
      if (clinicalRole && clinicalRole !== (user.clinical_role || '')) {
        await axios.patch(`${API}/users/${user.id}/clinical-role`,
          { clinical_role: clinicalRole }, { withCredentials: true });
      }

      // 3. Status (Active/Inactive). Self-deactivation is refused here too.
      if (!isSelf && active !== (user.active !== false)) {
        if (active) {
          await axios.put(`${API}/users/${user.id}/activate`, null, { withCredentials: true });
        } else {
          await axios.delete(`${API}/users/${user.id}`, { withCredentials: true });
        }
      }

      // 4. Licensing block — always sent so the PM can clear fields too.
      const licPayload = {
        ahpra_number: lic.ahpra_number || '',
        ahpra_expiry: lic.ahpra_expiry || '',
        asar_number: lic.asar_number || '',
        asar_expiry: lic.asar_expiry || '',
        radiation_licence_no: lic.radiation_licence_no || '',
        radiation_licence_expiry: lic.radiation_licence_expiry || '',
        bls_expiry: lic.bls_expiry || '',
        contrast_accreditation_expiry: lic.contrast_accreditation_expiry || '',
        accreditations: (lic.accreditations || '').split(',').map(s => s.trim()).filter(Boolean),
        cpd_hours_ytd: Number(lic.cpd_hours_ytd) || 0,
        cpd_year: Number(lic.cpd_year) || new Date().getFullYear(),
      };
      await axios.patch(`${API}/users/${user.id}/licensing`, licPayload, { withCredentials: true });

      setSavedFlag(true);
      // small delay so the user sees the green badge before the modal closes
      setTimeout(onSaved, 500);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Save failed.');
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="people-edit-modal">
      <div className="absolute inset-0 bg-black/50" onClick={busy ? undefined : onClose} />
      <div className="relative bg-background border border-border rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-5 shadow-2xl">
        {/* Header */}
        <div className="flex items-start justify-between gap-2 mb-4 pb-3 border-b border-border">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${ACCENT}15` }}>
              <Pencil className="w-4 h-4" style={{ color: ACCENT }} />
            </div>
            <div>
              <h3 className="font-['Outfit'] text-base font-bold text-foreground">Edit staff member</h3>
              <p className="text-[11px] text-muted-foreground font-mono">{user.email}</p>
            </div>
          </div>
          <button onClick={onClose} disabled={busy}
            className="text-muted-foreground hover:text-foreground p-1 rounded-md disabled:opacity-50"
            data-testid="people-edit-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Profile + role + status block */}
        <h4 className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">Profile</h4>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label className="text-[10px] font-semibold text-muted-foreground">Display name</label>
            <input value={name} onChange={e => setName(e.target.value)}
              data-testid="people-edit-name"
              className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2" />
          </div>
          <div>
            <label className="text-[10px] font-semibold text-muted-foreground">
              App role {isAdminTarget && <span className="text-red-500">(locked — change via DB)</span>}
            </label>
            <select value={role} onChange={e => setRole(e.target.value)}
              data-testid="people-edit-role"
              disabled={isAdminTarget}
              className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2 disabled:opacity-50">
              {isAdminTarget && <option value="admin">admin</option>}
              {ROLE_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <p className="text-[10px] text-muted-foreground mt-1">Permissions inside this app.</p>
          </div>
          <div>
            <label className="text-[10px] font-semibold text-muted-foreground">Clinical role</label>
            <select value={clinicalRole} onChange={e => setClinicalRole(e.target.value)}
              data-testid="people-edit-clinical-role"
              className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2">
              {CLINICAL_ROLE_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
            <p className="text-[10px] text-muted-foreground mt-1">
              Profession shown on DIAS / Kestral / licensing register. Pick <strong>Not a clinical staff member</strong> to hide admin-only logins from accreditation reports.
            </p>
          </div>
          <div className="col-span-2">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={active} disabled={isSelf}
                onChange={e => setActive(e.target.checked)}
                data-testid="people-edit-active"
                className="w-4 h-4 accent-current" />
              <span className="text-sm text-foreground">Active</span>
              {isSelf && <span className="text-[10px] text-muted-foreground italic">(can't deactivate yourself)</span>}
            </label>
          </div>
        </div>

        {/* Licensing block */}
        <h4 className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2 mt-4 pt-3 border-t border-border">
          Licensing &amp; certification
        </h4>

        {/* Annual practice-check audit panel. DIAS requires the practice to
            manually verify each clinician's external registrations every 12
            months — not just trust the AHPRA/EPA expiry date on file. This
            block shows when that was last done, when it's next due, and a
            one-click "Mark checked now" button that writes an immutable
            audit record + auto-rolls the review forward by 12 months. */}
        <PracticeCheckPanel
          checkedAt={practiceCheckedAt}
          reviewDueAt={practiceReviewDueAt}
          checkedBy={practiceCheckedBy}
          onMark={markPracticeChecked}
          marking={markingChecked}
        />

        {/* Iter 155.7 — SOP reassignment & completion audit trail.
            PM/admin can (re-)assign any non-draft SOP to this user for
            reinforcement (e.g. annual refresher, post-incident retrain).
            Every action is stamped `assigned_by / assigned_at / reason` on
            the sop_assignments row for a DIAS-defensible trail. */}
        <SopAssignmentsPanel userId={user.id} userName={user.name} />

        <div className="grid grid-cols-2 gap-3 mt-3">
          {LICENSING_FIELDS.map(f => (
            <div key={f.k}>
              <label className="text-[10px] font-semibold text-muted-foreground flex items-center justify-between gap-2">
                <span>{f.label}</span>
                {f.k === 'ahpra_number' && (
                  <a href={`https://www.ahpra.gov.au/Registration/Registers-of-Practitioners.aspx${user.name ? `?q=${encodeURIComponent(user.name)}` : ''}`}
                    target="_blank" rel="noopener noreferrer"
                    data-testid="people-edit-ahpra-verify"
                    title="Open AHPRA register in a new tab to verify this practitioner."
                    className="text-[10px] font-semibold text-cyan-600 hover:text-cyan-700 inline-flex items-center gap-0.5">
                    Verify on AHPRA →
                  </a>
                )}
                {f.k === 'asar_number' && (
                  <a href="https://www.asar.com.au/find-a-sonographer/"
                    target="_blank" rel="noopener noreferrer"
                    data-testid="people-edit-asar-verify"
                    title="Open the ASAR public register in a new tab."
                    className="text-[10px] font-semibold text-cyan-600 hover:text-cyan-700 inline-flex items-center gap-0.5">
                    Verify on ASAR →
                  </a>
                )}
                {f.k === 'radiation_licence_no' && (
                  <a href="https://nswepa-rst-prod1.pegacloud.net/prweb/PRSExt/app/public-register-search/6WNoKA_CO_C-2gC8GOWKWg*/!STANDARD?Regulatory=Radiation"
                    target="_blank" rel="noopener noreferrer"
                    data-testid="people-edit-epa-verify"
                    title="Open the NSW EPA Radiation public register in a new tab."
                    className="text-[10px] font-semibold text-cyan-600 hover:text-cyan-700 inline-flex items-center gap-0.5">
                    Verify on NSW EPA →
                  </a>
                )}
              </label>
              <input value={lic[f.k] || ''}
                onChange={e => setLicField(f.k, e.target.value)}
                type={f.type || 'text'} placeholder={f.placeholder || ''}
                data-testid={`people-edit-lic-${f.k}`}
                className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2" />
            </div>
          ))}
          <div className="col-span-2">
            <label className="text-[10px] font-semibold text-muted-foreground">
              Other accreditations (comma-separated, e.g. <em>NPS Pharmacology · CPD-MR-Safety</em>)
            </label>
            <input value={lic.accreditations}
              onChange={e => setLicField('accreditations', e.target.value)}
              data-testid="people-edit-lic-accreditations"
              className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2" />
          </div>
          <div>
            <label className="text-[10px] font-semibold text-muted-foreground">CPD hours YTD</label>
            <input type="number" step="0.5" min="0" max="500" value={lic.cpd_hours_ytd}
              onChange={e => setLicField('cpd_hours_ytd', e.target.value)}
              data-testid="people-edit-lic-cpd-hours"
              className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2" />
          </div>
          <div>
            <label className="text-[10px] font-semibold text-muted-foreground">CPD reporting year</label>
            <input type="number" min="2000" max="2100" value={lic.cpd_year}
              onChange={e => setLicField('cpd_year', e.target.value)}
              data-testid="people-edit-lic-cpd-year"
              className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2" />
          </div>
        </div>

        {/* Footer */}
        {err && (
          <div className="mt-4 rounded-lg border border-red-500/40 bg-red-500/10 p-2.5 text-xs text-red-600" data-testid="people-edit-error">
            {err}
          </div>
        )}
        {savedFlag && (
          <div className="mt-4 rounded-lg border border-green-500/40 bg-green-500/10 p-2.5 text-xs text-green-600 inline-flex items-center gap-1.5" data-testid="people-edit-saved">
            <CheckCircle2 className="w-3.5 h-3.5" /> Saved.
          </div>
        )}

        {canElevateToAdmin && (
          <ElevateToAdminBlock user={user} onElevated={onSaved} />
        )}
        <div className="flex items-center gap-2 justify-end mt-5 pt-4 border-t border-border">
          <button onClick={onClose} disabled={busy} data-testid="people-edit-cancel"
            className="text-sm px-3 py-1.5 rounded-lg border border-border disabled:opacity-50">
            Cancel
          </button>
          <button onClick={save} disabled={busy || !name.trim()}
            data-testid="people-edit-save"
            className="text-sm font-semibold px-4 py-1.5 rounded-lg text-white inline-flex items-center gap-1.5 disabled:opacity-40"
            style={{ backgroundColor: ACCENT }}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            {busy ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * In-place "Elevate to Admin" widget shown inside the edit-user modal.
 * Two-step confirmation:
 *   1. Click "Elevate to Admin" — shows a red warning + email-confirm input.
 *   2. Type the target's exact email → red "Confirm elevate" button enabled.
 * Server-side `require_admin` + `confirm_email` matching make this defensible.
 */
function ElevateToAdminBlock({ user, onElevated }) {
  const [step, setStep] = useState(0); // 0 = idle, 1 = confirming
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const expected = (user.email || '').trim().toLowerCase();
  const typedMatch = typed.trim().toLowerCase() === expected && expected.length > 0;

  const submit = async () => {
    if (!typedMatch) return;
    setBusy(true); setErr(null);
    try {
      await axios.post(`${API}/users/${user.id}/promote-to-admin`, null, {
        params: { confirm_email: typed.trim() },
        withCredentials: true,
      });
      onElevated();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Promotion failed.');
    }
    setBusy(false);
  };

  if (step === 0) {
    return (
      <div className="mt-5 pt-4 border-t border-border" data-testid="elevate-admin-block">
        <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Danger zone — admin elevation</p>
        <div className="rounded-lg border p-3 flex items-start justify-between gap-3" style={{ borderColor: '#DC262640', backgroundColor: '#DC26260A' }}>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground">Elevate {user.name || user.email} to <span style={{ color: '#DC2626' }}>Admin</span></p>
            <p className="text-[11px] text-muted-foreground mt-0.5">Admin role can permanently delete other users (including admins). Use sparingly.</p>
          </div>
          <button onClick={() => setStep(1)} data-testid="elevate-admin-start"
            className="text-xs font-semibold px-3 py-1.5 rounded-lg border shrink-0"
            style={{ borderColor: '#DC262640', color: '#DC2626' }}>
            Elevate to Admin
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-5 pt-4 border-t border-border" data-testid="elevate-admin-confirm">
      <div className="rounded-lg border p-3 space-y-2" style={{ borderColor: '#DC262680', backgroundColor: '#DC262610' }}>
        <p className="text-sm font-bold text-red-600">Confirm admin elevation</p>
        <ul className="text-[11px] text-foreground/80 list-disc list-inside space-y-0.5">
          <li>Admin can <strong>permanently delete</strong> other users (including other admins).</li>
          <li>Admin role cannot be downgraded via the UI — only via direct DB edit.</li>
          <li>This action is recorded in the governance audit log.</li>
        </ul>
        <div>
          <label className="text-[10px] font-semibold text-muted-foreground">Type the target's email to confirm</label>
          <input value={typed} onChange={e => setTyped(e.target.value)}
            data-testid="elevate-admin-confirm-input"
            placeholder={user.email}
            disabled={busy}
            className="w-full mt-1 text-sm font-mono bg-background border border-red-500/40 rounded-lg px-3 py-2 disabled:opacity-50" />
        </div>
        {err && <p className="text-[11px] text-red-600" data-testid="elevate-admin-error">{err}</p>}
        <div className="flex items-center justify-end gap-2">
          <button onClick={() => { setStep(0); setTyped(''); setErr(null); }} disabled={busy}
            data-testid="elevate-admin-cancel"
            className="text-xs px-2.5 py-1.5 rounded-lg border border-border disabled:opacity-50">Cancel</button>
          <button onClick={submit} disabled={!typedMatch || busy}
            data-testid="elevate-admin-confirm-btn"
            className="text-xs font-semibold px-3 py-1.5 rounded-lg text-white inline-flex items-center gap-1.5 disabled:opacity-30"
            style={{ backgroundColor: '#DC2626' }}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Shield className="w-3.5 h-3.5" />}
            {busy ? 'Elevating…' : 'Confirm elevate'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Two-warning destructive-action modal.
 *   Step 1 — read-and-acknowledge consequences (red banner with bullet list).
 *   Step 2 — type the user's email to confirm; only then is the red 'Delete
 *            permanently' button enabled.
 * The same `confirm_email` is sent to the backend as a query param, where it
 * is re-validated server-side as a final safety net.
 */
// Iter 130 — Admin password reset modals (mirrors the Permissions page UX).
function ResetPasswordConfirmModal({ user, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await axios.post(`${API}/auth/admin-reset-password`,
        { user_id: user.id, notify: true },
        { withCredentials: true });
      onDone({ user, ...r.data });
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not reset password');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} data-testid="people-reset-pw-confirm"
           className="bg-card rounded-xl border max-w-md w-full shadow-2xl p-5">
        <div className="flex items-center gap-2.5 mb-3">
          <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center">
            <KeyRound className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-lg font-bold" style={{ color: '#0d2644' }}>Reset password</h2>
            <div className="text-xs text-muted-foreground">{user.name || user.email}</div>
          </div>
        </div>
        <ul className="text-sm space-y-1.5 mb-4 list-disc pl-5 text-foreground/80">
          <li>A new temporary password will be generated.</li>
          <li>It will be <strong>emailed to {user.email}</strong> with login instructions.</li>
          <li>Any active sessions for this user will be invalidated immediately.</li>
          <li>The user must choose a new password on their next login.</li>
        </ul>
        {err && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-2.5 mb-3">{err}</div>
        )}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} disabled={busy} data-testid="people-reset-pw-cancel"
                  className="text-sm font-semibold px-4 py-1.5 rounded-lg border hover:bg-muted/40">
            Cancel
          </button>
          <button onClick={submit} disabled={busy} data-testid="people-reset-pw-confirm-btn"
                  className="text-sm font-semibold px-4 py-1.5 rounded-lg bg-amber-600 text-white inline-flex items-center gap-1.5 disabled:opacity-60">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />}
            {busy ? 'Resetting…' : 'Reset & email'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ResetPasswordResultModal({ data, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(data.temp_password);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} data-testid="people-reset-pw-result"
           className="bg-card rounded-xl border max-w-md w-full shadow-2xl p-5">
        <h2 className="text-lg font-bold mb-1" style={{ color: '#0d2644' }}>
          Password reset for {data.user.name || data.user.email}
        </h2>
        <div className={`text-xs mb-3 ${data.emailed ? 'text-green-700' : 'text-amber-700'}`}>
          {data.emailed
            ? `✓ Temp password emailed to ${data.user_email || data.user.email}.`
            : `⚠ Email did not send (${data.email_error || 'unknown'}). Share the temp password manually.`}
        </div>
        <div className="bg-[#0f172a] rounded-lg p-3 font-mono text-[#52c3c5] text-lg tracking-wider text-center font-bold mb-2"
             data-testid="people-reset-pw-temp">
          {data.temp_password}
        </div>
        <div className="flex items-center justify-between gap-2">
          <button onClick={copy}
                  className="text-xs font-semibold px-3 py-1.5 rounded border hover:bg-muted/40 inline-flex items-center gap-1.5">
            <CopyIcon className="w-3 h-3" /> {copied ? 'Copied' : 'Copy temp password'}
          </button>
          <button onClick={onClose}
                  className="text-xs font-semibold px-3 py-1.5 rounded bg-[#0d2644] text-white">
            Done
          </button>
        </div>
        <p className="text-[11px] text-muted-foreground mt-3 leading-relaxed">
          The user is now forced to choose a new password the next time they log in.
          This temp password is shown <strong>once</strong> — close this dialog and it&apos;s gone.
        </p>
      </div>
    </div>
  );
}


function DeleteUserModal({ user, onClose, onDeleted }) {
  const [step, setStep] = useState(1);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const expectedEmail = (user.email || '').trim().toLowerCase();
  const typedMatch = typed.trim().toLowerCase() === expectedEmail && expectedEmail.length > 0;

  const submit = async () => {
    if (!typedMatch) return;
    setBusy(true); setErr(null);
    try {
      await axios.delete(`${API}/users/${user.id}/permanent`, {
        params: { confirm_email: typed.trim() },
        withCredentials: true,
      });
      onDeleted();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Delete failed.');
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="people-delete-modal">
      <div className="absolute inset-0 bg-black/60" onClick={busy ? undefined : onClose} />
      <div className="relative bg-background border border-red-500/40 rounded-2xl w-full max-w-md p-5 shadow-2xl">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#DC262615' }}>
            <ShieldAlert className="w-4 h-4 text-red-600" />
          </div>
          <div>
            <h3 className="font-['Outfit'] text-base font-bold text-foreground">
              Permanently delete user
            </h3>
            <p className="text-[11px] text-muted-foreground">{user.name || user.email}</p>
          </div>
        </div>

        {step === 1 && (
          <div data-testid="people-delete-step-1">
            <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 mb-3">
              <p className="text-xs font-semibold text-red-600 mb-2 inline-flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5" /> Warning 1 of 2 — destructive &amp; irreversible
              </p>
              <ul className="list-disc pl-5 space-y-1 text-xs text-foreground/80">
                <li>The user&apos;s account will be <strong>permanently removed</strong> from the practice register.</li>
                <li>They will lose access to the app immediately — TOTP and password become inactive.</li>
                <li>Their SOP assignments and active invitation links (if any) are deleted with them.</li>
                <li>This <strong>cannot be undone</strong>. To temporarily disable instead, use the <em>Deactivate</em> action (sets <code>active: false</code>).</li>
                <li>For DIAS audit, immutable governance trails (culture-charter acceptance log, login activity, evidence-upload chain, attestations) <strong>are preserved</strong>.</li>
              </ul>
            </div>
            <div className="flex items-center gap-2 justify-end">
              <button onClick={onClose} data-testid="people-delete-cancel-1"
                className="text-sm px-3 py-1.5 rounded-lg border border-border">Cancel</button>
              <button onClick={() => setStep(2)} data-testid="people-delete-continue"
                className="text-sm font-semibold px-3 py-1.5 rounded-lg text-white bg-red-600 hover:bg-red-700 inline-flex items-center gap-1.5">
                Continue <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div data-testid="people-delete-step-2">
            <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 mb-3">
              <p className="text-xs font-semibold text-red-600 mb-2 inline-flex items-center gap-1.5">
                <ShieldAlert className="w-3.5 h-3.5" /> Warning 2 of 2 — final confirmation
              </p>
              <p className="text-xs text-foreground/80 mb-2">
                To proceed, type the user&apos;s email exactly to confirm you intend to permanently delete{' '}
                <strong>{user.name || user.email}</strong>.
              </p>
              <p className="text-[10px] font-mono text-muted-foreground mb-1">Expected: <span className="text-foreground">{expectedEmail}</span></p>
              <input value={typed} onChange={e => setTyped(e.target.value)} autoFocus
                data-testid="people-delete-email-input"
                placeholder="Type email here…"
                className="w-full text-sm bg-background border border-border rounded-lg px-3 py-2 font-mono"
                onKeyDown={e => { if (e.key === 'Enter' && typedMatch && !busy) submit(); }} />
            </div>
            {err && (
              <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-2 text-xs text-red-600 mb-3" data-testid="people-delete-error">
                {err}
              </div>
            )}
            <div className="flex items-center gap-2 justify-between">
              <button onClick={() => { setStep(1); setTyped(''); setErr(null); }} disabled={busy}
                data-testid="people-delete-back"
                className="text-xs px-3 py-1.5 rounded-lg border border-border disabled:opacity-50">
                ← Back
              </button>
              <div className="flex items-center gap-2">
                <button onClick={onClose} disabled={busy} data-testid="people-delete-cancel-2"
                  className="text-sm px-3 py-1.5 rounded-lg border border-border disabled:opacity-50">
                  Cancel
                </button>
                <button onClick={submit} disabled={!typedMatch || busy}
                  data-testid="people-delete-confirm"
                  className="text-sm font-semibold px-3 py-1.5 rounded-lg text-white bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5">
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                  {busy ? 'Deleting…' : 'Delete permanently'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Practice-check audit panel — used inside EditUserModal.
// Surfaces the annual practice-check status for a single staff member with
// a colour-coded status badge and a one-click "Mark checked now" button that
// writes an immutable audit record + auto-rolls the review forward 12 months.
// ─────────────────────────────────────────────────────────────────────────────
export function practiceCheckStatus(checkedAt, reviewDueAt) {
  if (!checkedAt) return { key: 'never', label: 'Never checked', color: '#0EA5E9', days: null };
  if (!reviewDueAt) return { key: 'never', label: 'Never checked', color: '#0EA5E9', days: null };
  const todayIso = new Date().toISOString().slice(0, 10);
  const d = new Date(reviewDueAt);
  const today = new Date(todayIso);
  const days = Math.round((d - today) / 86400000);
  if (days < 0) return { key: 'overdue', label: `Overdue ${Math.abs(days)}d`, color: '#DC2626', days };
  if (days <= 14) return { key: 'due_soon', label: `Due in ${days}d`, color: '#D97706', days };
  return { key: 'current', label: `OK · ${days}d`, color: '#16A34A', days };
}

export function PracticeCheckChip({ checkedAt, reviewDueAt }) {
  const s = practiceCheckStatus(checkedAt, reviewDueAt);
  return (
    <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded inline-flex items-center gap-1"
      style={{ backgroundColor: `${s.color}15`, color: s.color }}
      data-testid={`practice-check-chip-${s.key}`}
      title={
        s.key === 'never' ? 'No annual practice-check on record yet'
        : `Practice-check next review: ${reviewDueAt || '—'}`
      }>
      {s.label}
    </span>
  );
}

function PracticeCheckPanel({ checkedAt, reviewDueAt, checkedBy, onMark, marking }) {
  const s = practiceCheckStatus(checkedAt, reviewDueAt);
  const fmt = (iso) => iso ? new Date(iso).toLocaleDateString('en-AU', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
  return (
    <div className="mb-3 rounded-lg border p-3 flex items-start justify-between gap-3 flex-wrap"
      data-testid="people-edit-practice-check-panel"
      style={{ backgroundColor: `${s.color}08`, borderColor: `${s.color}40` }}>
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <ShieldCheck className="w-3.5 h-3.5" style={{ color: s.color }} />
          <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Annual practice-check</span>
          <PracticeCheckChip checkedAt={checkedAt} reviewDueAt={reviewDueAt} />
        </div>
        <p className="text-[11px] text-foreground/80">
          {checkedAt ? (
            <>
              Last verified <strong>{fmt(checkedAt)}</strong>
              {checkedBy ? <> by <strong>{checkedBy}</strong></> : null}
              {' · '}next review <strong>{fmt(reviewDueAt)}</strong>
            </>
          ) : (
            <>No practice-check on record yet. Open the AHPRA / EPA / ASAR registers above to verify, then mark checked.</>
          )}
        </p>
        <p className="text-[10px] text-muted-foreground mt-0.5">
          DIAS requires the practice to manually re-verify external registrations every 12 months.
        </p>
      </div>
      <button type="button" onClick={onMark} disabled={marking}
        data-testid="people-edit-mark-practice-checked"
        className="shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg text-white disabled:opacity-40"
        style={{ backgroundColor: s.color }}>
        {marking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
        {marking ? 'Saving…' : (checkedAt ? 'Mark re-checked now' : 'Mark checked now')}
      </button>
    </div>
  );
}

// Iter 155.7 — SOP re-assignment panel + audit trail (mounted inside EditUserModal).
function SopAssignmentsPanel({ userId, userName }) {
  const [state, setState] = useState({ loading: true, assignments: [], counts: {} });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setState(s => ({ ...s, loading: true }));
    try {
      const r = await axios.get(`${API}/sops/assignments/user/${userId}`, { withCredentials: true });
      setState({ loading: false, assignments: r.data.assignments || [], counts: r.data.counts || {} });
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load SOP history.');
      setState({ loading: false, assignments: [], counts: {} });
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

  const remove = async (aid) => {
    if (!window.confirm('Remove this assignment from the audit trail?')) return;
    try {
      await axios.delete(`${API}/sops/assignments/${aid}`, { withCredentials: true });
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to remove.');
    }
  };

  const { assignments, counts } = state;

  return (
    <div className="mt-4 pt-4 border-t border-border" data-testid="sop-assignments-panel">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <h4 className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          SOP assignments &amp; audit trail
        </h4>
        <button type="button" onClick={() => setPickerOpen(true)} data-testid="sop-reassign-btn"
          className="text-xs font-semibold px-3 py-1.5 rounded-lg border hover:bg-muted transition inline-flex items-center gap-1.5">
          <FilePlus2 className="w-3.5 h-3.5" /> Assign / re-assign SOPs
        </button>
      </div>
      {state.loading ? (
        <div className="text-xs text-muted-foreground py-4">Loading…</div>
      ) : (
        <>
          <div className="flex flex-wrap gap-2 text-[10px] mb-2" data-testid="sop-counts">
            <span className="px-2 py-0.5 rounded-full bg-muted font-semibold">Total: {counts.total || 0}</span>
            <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold">Pending: {counts.pending || 0}</span>
            <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-semibold">Completed: {counts.completed || 0}</span>
            {(counts.overdue || 0) > 0 && (
              <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-800 font-semibold">Overdue: {counts.overdue}</span>
            )}
          </div>
          {error && <div className="text-xs text-red-600 mb-2">{error}</div>}
          {assignments.length === 0 ? (
            <div className="text-xs text-muted-foreground py-4 text-center border border-dashed rounded-lg">
              No SOP assignments yet for {userName || 'this user'}.
            </div>
          ) : (
            <div className="max-h-72 overflow-y-auto border border-border rounded-lg divide-y">
              {assignments.map(a => {
                const badge = a.status === 'completed'
                  ? { cls: 'bg-emerald-100 text-emerald-800', text: 'completed' }
                  : a.overdue
                    ? { cls: 'bg-red-100 text-red-800', text: 'overdue' }
                    : { cls: 'bg-amber-100 text-amber-800', text: 'pending' };
                return (
                  <div key={a.assignment_id} className="px-3 py-2 text-xs" data-testid={`sop-assignment-${a.assignment_id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-foreground truncate">
                          {a.sop_title} <span className="text-muted-foreground font-normal">v{a.sop_version || '—'}</span>
                        </div>
                        <div className="text-[10px] text-muted-foreground mt-0.5">
                          Assigned {(a.assigned_at || '').slice(0, 10)} by {a.assigned_by || '—'} · Due {a.due_date || '—'}
                          {a.status === 'completed' && a.completed_at && (
                            <> · <span className="text-emerald-700 font-semibold">completed {a.completed_at.slice(0, 10)}</span></>
                          )}
                        </div>
                        {a.reason && (
                          <div className="text-[10px] italic text-muted-foreground mt-0.5">reason: {a.reason}</div>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${badge.cls}`}>{badge.text}</span>
                        <button type="button" onClick={() => remove(a.assignment_id)}
                          data-testid={`sop-assignment-delete-${a.assignment_id}`}
                          title="Remove from audit trail"
                          className="text-red-500 hover:text-red-700 p-1">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
      {pickerOpen && (
        <SopReassignPickerModal
          userId={userId}
          userName={userName}
          onClose={() => setPickerOpen(false)}
          onDone={async () => { setPickerOpen(false); await load(); }} />
      )}
    </div>
  );
}


function SopReassignPickerModal({ userId, userName, onClose, onDone }) {
  const [sops, setSops] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [dueDate, setDueDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 30);
    return d.toISOString().slice(0, 10);
  });
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [filter, setFilter] = useState('');

  useEffect(() => {
    axios.get(`${API}/sops`, { withCredentials: true })
      .then(r => setSops(r.data || []))
      .catch(e => setErr(e?.response?.data?.detail || 'Failed to load SOPs.'));
  }, []);

  const toggle = (sid) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(sid)) next.delete(sid); else next.add(sid);
      return next;
    });
  };

  const submit = async () => {
    if (selected.size === 0) { setErr('Pick at least one SOP.'); return; }
    if (!dueDate) { setErr('Set a due date.'); return; }
    setBusy(true); setErr('');
    try {
      const r = await axios.post(`${API}/sops/assign/bulk`,
        { user_id: userId, sop_ids: [...selected], due_date: dueDate, reason },
        { withCredentials: true });
      if (r.data.skipped_drafts?.length) {
        alert(`Skipped ${r.data.skipped_drafts.length} draft SOP(s):\n\n${r.data.skipped_drafts.join('\n')}`);
      }
      await onDone();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to assign.');
    } finally { setBusy(false); }
  };

  const filtered = (sops || []).filter(s => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (s.title || '').toLowerCase().includes(q)
        || (s.category || '').toLowerCase().includes(q);
  });

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" onClick={onClose} data-testid="sop-reassign-modal">
      <div onClick={e => e.stopPropagation()} className="bg-card border border-border rounded-2xl max-w-2xl w-full p-5 max-h-[90vh] overflow-y-auto">
        <h3 className="font-['Outfit'] text-lg font-bold text-foreground mb-3">Assign / re-assign SOPs — {userName || ''}</h3>
        <p className="text-xs text-muted-foreground mb-3">
          Pick one or more SOPs to (re-)assign. Each selection creates a new audit row, so past history is preserved.
          Draft SOPs are skipped automatically.
        </p>
        <div className="grid grid-cols-2 gap-2 mb-3">
          <label className="text-xs">
            <span className="text-muted-foreground">Due date</span>
            <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)}
              data-testid="sop-reassign-due-date"
              className="w-full mt-1 text-sm border rounded-lg px-3 py-2 bg-background" />
          </label>
          <label className="text-xs">
            <span className="text-muted-foreground">Reason (audit note)</span>
            <input type="text" value={reason} onChange={e => setReason(e.target.value)}
              data-testid="sop-reassign-reason"
              placeholder="e.g. annual refresher, post-incident retrain"
              className="w-full mt-1 text-sm border rounded-lg px-3 py-2 bg-background" />
          </label>
        </div>
        <input type="text" value={filter} onChange={e => setFilter(e.target.value)}
          data-testid="sop-reassign-filter"
          placeholder="Filter SOPs by title or category…"
          className="w-full text-sm border rounded-lg px-3 py-2 bg-background mb-2" />
        {sops === null ? (
          <div className="text-xs text-muted-foreground py-6 text-center">Loading SOPs…</div>
        ) : (
          <div className="border border-border rounded-lg divide-y max-h-64 overflow-y-auto">
            {filtered.map(s => {
              const draft = ((s.version || '').toLowerCase().includes('tba')
                || (s.version || '').toLowerCase().startsWith('draft v0')
                || (s.version || '').toLowerCase().includes('draft'));
              const on = selected.has(s.sop_id);
              return (
                <button type="button" key={s.sop_id} onClick={() => !draft && toggle(s.sop_id)} disabled={draft}
                  data-testid={`sop-reassign-pick-${s.sop_id}`}
                  className={`w-full text-left px-3 py-2 flex items-center gap-2 text-sm hover:bg-muted/60 transition ${on ? 'bg-emerald-50/60 dark:bg-emerald-950/20' : ''} ${draft ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  <input type="checkbox" checked={on} readOnly className="pointer-events-none" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{s.title}</div>
                    <div className="text-[10px] text-muted-foreground">
                      {s.category} · v{s.version || '—'}{draft && <span className="ml-2 text-amber-600 font-semibold">draft — cannot assign</span>}
                    </div>
                  </div>
                </button>
              );
            })}
            {filtered.length === 0 && <div className="text-xs text-muted-foreground py-4 text-center">No SOPs match.</div>}
          </div>
        )}
        {err && <div className="text-xs text-red-600 mt-2">{err}</div>}
        <div className="flex items-center justify-between mt-4">
          <div className="text-xs text-muted-foreground">
            {selected.size} SOP{selected.size === 1 ? '' : 's'} selected
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="text-sm px-3 py-2 rounded-lg border" data-testid="sop-reassign-cancel">
              Cancel
            </button>
            <button type="button" onClick={submit} disabled={busy || selected.size === 0}
              data-testid="sop-reassign-submit"
              className="text-sm px-3 py-2 rounded-lg font-semibold text-white disabled:opacity-50"
              style={{ backgroundColor: '#0F766E' }}>
              {busy ? 'Assigning…' : `Assign ${selected.size || ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}




export function LicensingTable({ users, expiring }) {
  const expiringIds = new Set(expiring.map(e => e.user_id));
  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden" data-testid="people-licensing-table">
      <table className="w-full text-sm">
        <thead className="bg-muted/40">
          <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            <th className="px-4 py-3">Name</th>
            <th className="px-4 py-3">AHPRA #</th>
            <th className="px-4 py-3">AHPRA</th>
            <th className="px-4 py-3">Rad. Licence</th>
            <th className="px-4 py-3">Rad.</th>
            <th className="px-4 py-3">BLS</th>
            <th className="px-4 py-3">Contrast</th>
            <th className="px-4 py-3" title="Annual practice-check: when the practice last manually verified this clinician's external registrations.">Practice check</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u, i) => {
            const lic = u.licensing || {};
            return (
              <tr key={u.id} className="border-t border-border" data-testid={`licensing-row-${i}`}>
                <td className="px-4 py-2.5">
                  <div className="font-medium">{u.name}</div>
                  <div className="text-[10px] text-muted-foreground">{u.role}</div>
                </td>
                <td className="px-4 py-2.5 font-mono text-xs">{lic.ahpra_number || '—'}</td>
                <td className="px-4 py-2.5"><StatusChip dateStr={lic.ahpra_expiry} /></td>
                <td className="px-4 py-2.5 font-mono text-xs">{lic.radiation_licence_number || '—'}</td>
                <td className="px-4 py-2.5"><StatusChip dateStr={lic.radiation_licence_expiry} /></td>
                <td className="px-4 py-2.5"><StatusChip dateStr={lic.bls_expiry} /></td>
                <td className="px-4 py-2.5"><StatusChip dateStr={lic.contrast_accreditation_expiry} /></td>
                <td className="px-4 py-2.5">
                  <PracticeCheckChip
                    checkedAt={lic.practice_checked_at}
                    reviewDueAt={lic.practice_review_due_at} />
                </td>
              </tr>
            );
          })}
          {users.length === 0 && (
            <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-muted-foreground">No staff registered yet.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function SOPAssignmentsTable({ users, assignments }) {
  // Group assignments by user
  const grouped = useMemo(() => {
    const map = {};
    for (const a of assignments) {
      const id = a.user_id;
      if (!map[id]) map[id] = { total: 0, completed: 0, overdue: 0 };
      map[id].total += 1;
      if (a.completed_at) map[id].completed += 1;
      if (!a.completed_at && a.due_date && a.due_date < today) map[id].overdue += 1;
    }
    return map;
  }, [assignments]);

  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden" data-testid="people-sops-table">
      <table className="w-full text-sm">
        <thead className="bg-muted/40">
          <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            <th className="px-4 py-3">Name</th>
            <th className="px-4 py-3">Role</th>
            <th className="px-4 py-3">Total assigned</th>
            <th className="px-4 py-3">Completed</th>
            <th className="px-4 py-3">Overdue</th>
            <th className="px-4 py-3">% complete</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u, i) => {
            const stats = grouped[u.id] || { total: 0, completed: 0, overdue: 0 };
            const pct = stats.total ? Math.round(stats.completed / stats.total * 100) : 0;
            const pctColor = pct === 100 ? '#16A34A' : pct >= 70 ? '#0EA5E9' : pct >= 40 ? '#D97706' : '#DC2626';
            return (
              <tr key={u.id} className="border-t border-border" data-testid={`sops-row-${i}`}>
                <td className="px-4 py-2.5 font-medium">{u.name}</td>
                <td className="px-4 py-2.5 text-xs">{u.role}</td>
                <td className="px-4 py-2.5 tabular-nums">{stats.total}</td>
                <td className="px-4 py-2.5 tabular-nums text-green-600">{stats.completed}</td>
                <td className="px-4 py-2.5 tabular-nums" style={{ color: stats.overdue > 0 ? '#DC2626' : 'inherit' }}>{stats.overdue}</td>
                <td className="px-4 py-2.5">
                  {stats.total === 0 ? (
                    <span className="text-[10px] text-muted-foreground">—</span>
                  ) : (
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden max-w-[80px]">
                        <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: pctColor }} />
                      </div>
                      <span className="text-[11px] font-bold tabular-nums" style={{ color: pctColor }}>{pct}%</span>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function OnboardingTable({ users }) {
  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden" data-testid="people-onboarding-table">
      <table className="w-full text-sm">
        <thead className="bg-muted/40">
          <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            <th className="px-4 py-3">Name</th>
            <th className="px-4 py-3">Account</th>
            <th className="px-4 py-3">2FA</th>
            <th className="px-4 py-3">Charter</th>
            <th className="px-4 py-3">Licensing</th>
            <th className="px-4 py-3">Onboarding %</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u, i) => {
            const lic = u.licensing || {};
            const hasLic = !!(lic.ahpra_number || lic.radiation_licence_number);
            const steps = [
              { k: 'account', done: !!u.email },
              { k: 'totp', done: u.totp_enabled },
              { k: 'charter', done: !!u.culture_charter_accepted_at },
              { k: 'licensing', done: hasLic },
            ];
            const done = steps.filter(s => s.done).length;
            const pct = Math.round(done / steps.length * 100);
            const pctColor = pct === 100 ? '#16A34A' : pct >= 75 ? '#0EA5E9' : pct >= 50 ? '#D97706' : '#DC2626';
            return (
              <tr key={u.id} className="border-t border-border" data-testid={`onboarding-row-${i}`}>
                <td className="px-4 py-2.5 font-medium">{u.name}<div className="text-[10px] text-muted-foreground">{u.email}</div></td>
                <td className="px-4 py-2.5">{u.email ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <XCircle className="w-4 h-4 text-muted-foreground" />}</td>
                <td className="px-4 py-2.5">{u.totp_enabled ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <XCircle className="w-4 h-4 text-muted-foreground" />}</td>
                <td className="px-4 py-2.5">{u.culture_charter_accepted_at ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <XCircle className="w-4 h-4 text-muted-foreground" />}</td>
                <td className="px-4 py-2.5">{hasLic ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <XCircle className="w-4 h-4 text-muted-foreground" />}</td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden max-w-[80px]">
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: pctColor }} />
                    </div>
                    <span className="text-[11px] font-bold tabular-nums" style={{ color: pctColor }}>{pct}%</span>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function KPI({ icon: Icon, label, value, color, sublabel }) {
  return (
    <div className="bg-card border border-border rounded-2xl p-4" data-testid={`people-kpi-${label.replace(/\W+/g, '-').toLowerCase()}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-3.5 h-3.5" style={{ color }} />
        <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</span>
      </div>
      <div className="text-2xl font-bold tabular-nums" style={{ color }}>{value}</div>
      {sublabel && <p className="text-[10px] text-muted-foreground mt-0.5">{sublabel}</p>}
    </div>
  );
}

/**
 * Bulk-edit licensing modal.
 *
 * Lets a PM apply the SAME licensing-expiry update to many staff records at
 * once — typical use case: "I just hired 4 radiographers from the same
 * training intake; their AHPRA expiry is the same date." The PM ticks the
 * rows, opens the modal, fills only the fields they want changed, and Save
 * calls POST /api/users/bulk-licensing.
 *
 * Important UX rules:
 *  • Only the fields the PM types into are sent. Empty inputs = "leave that
 *    field unchanged on every selected user". The backend mirrors this by
 *    discarding empty string values.
 *  • The modal explicitly lists the selected names so the PM can sanity-check
 *    before pressing Save.
 *  • Confirmation step on Save — shows "About to write <fields> to N users"
 *    and asks for an explicit Confirm before firing.
 */
const BULK_FIELDS = [
  { k: 'ahpra_number',                  label: 'AHPRA registration #',           type: 'text', placeholder: 'MED0001234567' },
  { k: 'ahpra_expiry',                  label: 'AHPRA expiry',                   type: 'date' },
  { k: 'asar_number',                   label: 'ASAR # (sonographers)',          type: 'text' },
  { k: 'asar_expiry',                   label: 'ASAR expiry',                    type: 'date' },
  { k: 'radiation_licence_no',          label: 'Radiation licence #',            type: 'text' },
  { k: 'radiation_licence_expiry',      label: 'Radiation licence expiry',       type: 'date' },
  { k: 'bls_expiry',                    label: 'BLS / CPR expiry',               type: 'date' },
  { k: 'contrast_accreditation_expiry', label: 'Contrast accreditation expiry',  type: 'date' },
];

function BulkLicensingModal({ users, onClose, onSaved }) {
  const [fields, setFields] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [confirmStep, setConfirmStep] = useState(false);
  const [result, setResult] = useState(null);

  const setField = (k, v) => setFields(prev => ({ ...prev, [k]: v }));
  const filledFields = Object.entries(fields).filter(([_, v]) => v && String(v).trim() !== '');

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      const payload = {
        user_ids: users.map(u => u.id),
        fields: Object.fromEntries(filledFields.map(([k, v]) => [k, String(v).trim()])),
      };
      const r = await axios.post(`${API}/users/bulk-licensing`, payload, { withCredentials: true });
      setResult(r.data);
      setTimeout(onSaved, 800);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Bulk update failed.');
      setBusy(false);
      setConfirmStep(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="people-bulk-modal">
      <div className="absolute inset-0 bg-black/50" onClick={busy ? undefined : onClose} />
      <div className="relative bg-background border border-border rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-5 shadow-2xl">
        <div className="flex items-start justify-between gap-2 mb-4 pb-3 border-b border-border">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${ACCENT}15` }}>
              <Pencil className="w-4 h-4" style={{ color: ACCENT }} />
            </div>
            <div>
              <h3 className="font-['Outfit'] text-base font-bold text-foreground">Bulk-edit licensing</h3>
              <p className="text-[11px] text-muted-foreground">{users.length} staff selected · only filled fields are written</p>
            </div>
          </div>
          <button onClick={onClose} disabled={busy}
            className="text-muted-foreground hover:text-foreground p-1 rounded-md disabled:opacity-50"
            data-testid="people-bulk-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Selected names */}
        <div className="mb-4 rounded-lg border border-border p-2.5 bg-muted/40 max-h-32 overflow-y-auto" data-testid="people-bulk-selected-list">
          <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Applying to</div>
          <div className="flex flex-wrap gap-1.5">
            {users.map(u => (
              <span key={u.id} className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-background border border-border">
                {u.name || u.email}
                <span className="text-muted-foreground ml-1">· {u.role}</span>
              </span>
            ))}
          </div>
        </div>

        {result ? (
          <div className="rounded-lg border border-green-500/40 bg-green-500/10 p-3 text-xs text-green-700" data-testid="people-bulk-result">
            <CheckCircle2 className="w-4 h-4 inline mr-1" />
            Updated <strong>{result.modified}</strong> of <strong>{result.matched}</strong> selected users
            {result.fields_applied?.length > 0 && (
              <> · fields applied: <strong>{result.fields_applied.join(', ')}</strong></>
            )}
          </div>
        ) : !confirmStep ? (
          <>
            <h4 className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">
              Fields to update <span className="text-muted-foreground">(leave empty to keep unchanged)</span>
            </h4>
            <div className="grid grid-cols-2 gap-3">
              {BULK_FIELDS.map(f => (
                <div key={f.k}>
                  <label className="text-[10px] font-semibold text-muted-foreground">{f.label}</label>
                  <input value={fields[f.k] || ''} type={f.type} placeholder={f.placeholder || ''}
                    onChange={e => setField(f.k, e.target.value)}
                    data-testid={`people-bulk-${f.k}`}
                    className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2" />
                </div>
              ))}
            </div>
            {err && <div className="mt-3 rounded-lg border border-red-500/40 bg-red-500/10 p-2.5 text-xs text-red-600" data-testid="people-bulk-error">{err}</div>}
            <div className="flex items-center justify-end gap-2 mt-5 pt-4 border-t border-border">
              <button onClick={onClose} className="text-sm px-3 py-1.5 rounded-lg border border-border" data-testid="people-bulk-cancel">
                Cancel
              </button>
              <button onClick={() => setConfirmStep(true)}
                disabled={filledFields.length === 0}
                data-testid="people-bulk-continue"
                className="text-sm font-semibold px-4 py-1.5 rounded-lg text-white inline-flex items-center gap-1.5 disabled:opacity-40"
                style={{ backgroundColor: ACCENT }}>
                Review &amp; apply <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </>
        ) : (
          <div data-testid="people-bulk-confirm">
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 mb-4">
              <p className="text-xs font-semibold text-amber-700 mb-2 inline-flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5" /> Confirm bulk update
              </p>
              <p className="text-xs text-foreground/80 mb-2">
                The following <strong>{filledFields.length}</strong> field{filledFields.length === 1 ? '' : 's'} will be written to{' '}
                <strong>{users.length}</strong> selected user{users.length === 1 ? '' : 's'}:
              </p>
              <ul className="text-xs space-y-1 font-mono text-foreground/80">
                {filledFields.map(([k, v]) => (
                  <li key={k}>• <span className="text-muted-foreground">{k}</span> = <strong>{v}</strong></li>
                ))}
              </ul>
              <p className="text-[10px] text-muted-foreground mt-2">
                Other licensing fields on each user are NOT touched.
              </p>
            </div>
            {err && <div className="mb-3 rounded-lg border border-red-500/40 bg-red-500/10 p-2.5 text-xs text-red-600" data-testid="people-bulk-error">{err}</div>}
            <div className="flex items-center justify-between gap-2 pt-3 border-t border-border">
              <button onClick={() => setConfirmStep(false)} disabled={busy}
                data-testid="people-bulk-back"
                className="text-xs px-3 py-1.5 rounded-lg border border-border disabled:opacity-50">
                ← Back
              </button>
              <div className="flex items-center gap-2">
                <button onClick={onClose} disabled={busy}
                  className="text-sm px-3 py-1.5 rounded-lg border border-border disabled:opacity-50">
                  Cancel
                </button>
                <button onClick={submit} disabled={busy} data-testid="people-bulk-confirm-apply"
                  className="text-sm font-semibold px-4 py-1.5 rounded-lg text-white inline-flex items-center gap-1.5 disabled:opacity-40"
                  style={{ backgroundColor: ACCENT }}>
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                  {busy ? 'Applying…' : `Apply to ${users.length} user${users.length === 1 ? '' : 's'}`}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Medicare provider-number cell rendered per staff row.
 *
 * 4 visual states:
 *   • saved (high_confidence OR manual): green chip with the provider number
 *   • needs_review:   yellow chip with the candidate count + "Pick" button
 *   • no_match:       grey "No match" chip + Re-lookup
 *   • skipped/unknown: tiny "—" placeholder
 *
 * For non-clinical roles (admin/reception/superuser) the cell shows a dash.
 */
function MedicareCell({ user, isPM, onPick, onLookup, busy }) {
  const lic = user.licensing || {};
  const ml  = lic.medicare_lookup || {};
  const pn  = lic.medicare_provider_number;
  const status = ml.status || (pn ? 'manual' : 'unknown');

  const role = (user.role || '').toLowerCase();
  const clinicalRoles = ['radiologist', 'registrar', 'sonographer', 'radiographer', 'rso', 'nurse'];
  const isClinical = clinicalRoles.includes(role);
  if (!isClinical) {
    return <span className="text-[10px] text-muted-foreground/60">—</span>;
  }

  if (pn) {
    return (
      <div className="flex items-center gap-1.5" data-testid={`med-saved-${user.id}`}>
        <span className="font-mono text-[11px] font-bold px-1.5 py-0.5 rounded"
          style={{ backgroundColor: '#16A34A15', color: '#16A34A' }}>
          {pn}
        </span>
        {isPM && (
          <button onClick={onLookup} disabled={busy}
            data-testid={`med-relookup-${user.id}`}
            title="Re-run RCH search to find newer provider numbers"
            className="p-1 text-muted-foreground/60 hover:text-foreground disabled:opacity-50">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          </button>
        )}
      </div>
    );
  }

  if (status === 'needs_review') {
    const n = ml.candidates_count || ml.alternates?.length || 0;
    return (
      <button onClick={onPick} disabled={!isPM}
        data-testid={`med-review-${user.id}`}
        className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full inline-flex items-center gap-1 hover:opacity-80 disabled:cursor-default"
        style={{ backgroundColor: '#D9770615', color: '#D97706' }}>
        <AlertTriangle className="w-3 h-3" /> {n} matches · Pick
      </button>
    );
  }

  if (status === 'no_match') {
    return (
      <div className="flex items-center gap-1.5" data-testid={`med-nomatch-${user.id}`}>
        <span className="text-[10px] font-semibold text-muted-foreground">No match</span>
        {isPM && (
          <button onClick={onLookup} disabled={busy}
            data-testid={`med-relookup-${user.id}`}
            title="Search again (e.g. after editing the user's name)"
            className="p-1 text-muted-foreground/60 hover:text-foreground disabled:opacity-50">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
          </button>
        )}
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="flex items-center gap-1.5">
        <span className="text-[10px] font-semibold text-red-500" title={ml.error} data-testid={`med-error-${user.id}`}>Lookup error</span>
        {isPM && (
          <button onClick={onLookup} disabled={busy}
            data-testid={`med-relookup-${user.id}`}
            className="p-1 text-muted-foreground/60 hover:text-foreground disabled:opacity-50">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          </button>
        )}
      </div>
    );
  }

  // unknown / never-run yet (rare since auto-attach fires on create)
  return isPM ? (
    <button onClick={onLookup} disabled={busy}
      data-testid={`med-lookup-${user.id}`}
      className="text-[10px] font-semibold inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-border text-muted-foreground hover:bg-muted/40">
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
      Look up
    </button>
  ) : <span className="text-[10px] text-muted-foreground/60">—</span>;
}

/**
 * Provider-number pick modal.
 *
 * Shows candidates from the user's last RCH lookup, and lets the PM:
 *  • Pick a candidate row (saves via /users/{id}/medicare-pick)
 *  • Tweak surname / first-name and Search again (proxies through
 *    /api/medicare/search) — useful when the auto-name-split was wrong.
 *  • Filter by practitioner type matching the user's role
 *    (auto-narrowed default; toggle to show all).
 */
function MedicarePickModal({ user, onClose, onSaved }) {
  const initialLookup = user.licensing?.medicare_lookup || {};
  const [results, setResults] = useState(initialLookup.alternates || []);
  const [narrowedOnly, setNarrowedOnly] = useState(true);
  // Pre-fill surname/first using the same last-token rule the backend uses.
  const parts = (user.name || '').trim().split(/\s+/);
  const [surname, setSurname] = useState(parts[parts.length - 1] || '');
  const [firstName, setFirstName] = useState(parts.length > 1 ? parts[0] : '');
  const [recordsTotal, setRecordsTotal] = useState(initialLookup.candidates_count || results.length);
  const [busy, setBusy] = useState(false);
  const [savingPn, setSavingPn] = useState(null);
  const [err, setErr] = useState(null);

  const ROLE_KEYWORDS = {
    radiologist:  ['radiologist', 'radiology'],
    registrar:    ['registrar'],
    sonographer:  ['sonographer', 'ultrasound'],
    radiographer: ['radiographer'],
    rso:          ['radiation safety'],
    nurse:        ['nurse'],
  };
  const keywords = ROLE_KEYWORDS[(user.role || '').toLowerCase()] || [];
  const filtered = narrowedOnly && keywords.length > 0
    ? results.filter(r => {
        const t = (r.practitioner_type || '').toLowerCase();
        return keywords.some(k => t.includes(k));
      })
    : results;

  const search = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await axios.get(`${API}/medicare/search`, {
        params: { surname, first_name: firstName, search_aussie: true, role_hint: user.role },
        withCredentials: true,
      });
      setResults(r.data.results || []);
      setRecordsTotal(r.data.records_total || 0);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Search failed.');
    }
    setBusy(false);
  };

  const pick = async (row) => {
    setSavingPn(row.provider_number); setErr(null);
    try {
      await axios.post(`${API}/users/${user.id}/medicare-pick`, {
        provider_number: row.provider_number,
        practitioner_type: row.practitioner_type || '',
        address: row.address || '',
      }, { withCredentials: true });
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Save failed.');
      setSavingPn(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="people-medicare-modal">
      <div className="absolute inset-0 bg-black/50" onClick={busy ? undefined : onClose} />
      <div className="relative bg-background border border-border rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-5 shadow-2xl">
        <div className="flex items-start justify-between gap-2 mb-4 pb-3 border-b border-border">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${ACCENT}15` }}>
              <Stethoscope className="w-4 h-4" style={{ color: ACCENT }} />
            </div>
            <div>
              <h3 className="font-['Outfit'] text-base font-bold text-foreground">Pick Medicare provider number</h3>
              <p className="text-[11px] text-muted-foreground">{user.name} · {user.role}</p>
            </div>
          </div>
          <button onClick={onClose} disabled={busy} data-testid="people-medicare-close"
            className="text-muted-foreground hover:text-foreground p-1 rounded-md disabled:opacity-50">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Search controls */}
        <div className="grid grid-cols-12 gap-2 mb-3">
          <div className="col-span-5">
            <label className="text-[10px] font-semibold text-muted-foreground">Surname</label>
            <input value={surname} onChange={e => setSurname(e.target.value)}
              data-testid="people-medicare-surname"
              className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2" />
          </div>
          <div className="col-span-4">
            <label className="text-[10px] font-semibold text-muted-foreground">First name (initial OK)</label>
            <input value={firstName} onChange={e => setFirstName(e.target.value)}
              data-testid="people-medicare-firstname"
              className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2" />
          </div>
          <div className="col-span-3 flex items-end">
            <button onClick={search} disabled={busy || !surname.trim()}
              data-testid="people-medicare-search-btn"
              className="w-full inline-flex items-center justify-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg text-white disabled:opacity-50"
              style={{ backgroundColor: ACCENT }}>
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
              {busy ? 'Searching…' : 'Search RCH'}
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 mb-2 text-xs">
          <div className="text-muted-foreground" data-testid="people-medicare-summary">
            {recordsTotal} record{recordsTotal === 1 ? '' : 's'} returned
            {filtered.length !== results.length && <> · showing {filtered.length} after role filter</>}
          </div>
          {keywords.length > 0 && (
            <label className="inline-flex items-center gap-1.5 cursor-pointer text-muted-foreground">
              <input type="checkbox" checked={narrowedOnly} onChange={e => setNarrowedOnly(e.target.checked)}
                data-testid="people-medicare-narrow"
                className="w-3.5 h-3.5 accent-current" />
              Match by role ({user.role})
            </label>
          )}
        </div>

        {err && <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-2.5 text-xs text-red-600 mb-3" data-testid="people-medicare-error">{err}</div>}

        {/* Results table */}
        <div className="border border-border rounded-2xl overflow-hidden" data-testid="people-medicare-results">
          <div className="overflow-x-auto max-h-96">
            <table className="w-full text-xs">
              <thead className="bg-muted/40 sticky top-0">
                <tr className="text-left">
                  <th className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Name</th>
                  <th className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Provider #</th>
                  <th className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Type</th>
                  <th className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Address</th>
                  <th className="px-3 py-2 text-right" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered.length === 0 && (
                  <tr><td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">
                    {results.length === 0
                      ? 'No results yet — adjust the surname / first name and click "Search RCH".'
                      : 'No results match your role filter. Untick "Match by role" to see all.'}
                  </td></tr>
                )}
                {filtered.map((r, i) => (
                  <tr key={`${r.provider_number}-${i}`} className="hover:bg-muted/20" data-testid={`med-result-${r.provider_number}`}>
                    <td className="px-3 py-2">
                      <div className="font-semibold">{r.surname} {r.given_names}</div>
                    </td>
                    <td className="px-3 py-2 font-mono font-bold">{r.provider_number}</td>
                    <td className="px-3 py-2 text-muted-foreground">{r.practitioner_type || '—'}</td>
                    <td className="px-3 py-2 text-muted-foreground">{r.address || '—'}</td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => pick(r)} disabled={savingPn !== null}
                        data-testid={`med-pick-${r.provider_number}`}
                        className="text-[11px] font-semibold inline-flex items-center gap-1 px-2 py-1 rounded-md text-white disabled:opacity-50"
                        style={{ backgroundColor: ACCENT }}>
                        {savingPn === r.provider_number ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                        {savingPn === r.provider_number ? 'Saving…' : 'Pick'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 mt-4 pt-3 border-t border-border">
          <button onClick={onClose} disabled={busy || savingPn !== null}
            data-testid="people-medicare-cancel"
            className="text-sm px-3 py-1.5 rounded-lg border border-border disabled:opacity-50">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
